import { useCallback, useEffect } from 'react';
import type { QuickFillItem } from '../../../shared/types';
import type { FindReplaceOptions, SearchMatch } from '../../../shared/lib/utils/findReplaceUtils';
import type { ImportPreview, ImportResult } from '../../../shared/lib/utils/csvUtils';
import { getAutoFillPreview, type QuickFillAutoFillMode } from '../../../shared/lib/utils/autoFillService';
import {
  buildEditedQuickFillCellUpdates,
  type QuickFillConfiguredBindingKind,
} from '../lib/quickFillPageModel';
import type { QuickFillSelectedCell } from '../lib/quickFillNavigationModel';
import type {
  QuickFillSubmitOutcome,
  QuickFillTableCellStageResult,
  QuickFillTableCellStageUpdate,
} from '../lib/quickFillTableOperationHelpers';
import {
  useQuickFillMutationOperations,
  type QuickFillMutationOperations,
} from './useQuickFillMutationOperations';
import {
  useQuickFillClipboardOperations,
  type QuickFillClearTarget,
} from './useQuickFillClipboardOperations';
import { useQuickFillFindReplaceOperations } from './useQuickFillFindReplaceOperations';
import { useQuickFillCsvOperations } from './useQuickFillCsvOperations';
import { useQuickFillAutoFillOperations } from './useQuickFillAutoFillOperations';

export type { QuickFillClearTarget } from './useQuickFillClipboardOperations';

type AutoFillApplyResult = ReturnType<QuickFillMutationOperations['autoFillRowsWithLabels']>;
type SyncProgress = QuickFillMutationOperations['revitSync']['progress'];

export interface UseQuickFillTableOperationsResult {
  selectedRows: number[];
  replaceSelectedRows: (rowIndices: number[]) => void;
  handleTableDataChange: (
    index: number, field: keyof QuickFillItem, value: string
  ) => Promise<void>;
  stageCellUpdates: (
    updates: QuickFillTableCellStageUpdate[], label?: string
  ) => QuickFillTableCellStageResult;
  handleSelectRow: (index: number) => void;
  handleSelectAllRows: (rowIndices?: number[]) => void;
  handleBulkEdit: (
    field: keyof QuickFillItem, value: string, rowIndices?: number[]
  ) => Promise<void>;
  handleCopySelected: (cells?: QuickFillSelectedCell[]) => Promise<void>;
  handlePaste: (targetCells?: QuickFillSelectedCell[]) => Promise<void>;
  handleClearSelectedCells: (target?: QuickFillClearTarget) => void;
  handleFillDown: () => Promise<void>;
  handleFillRight: () => Promise<void>;
  handleCut: () => Promise<void>;
  handleFind: (searchTerm: string, options: FindReplaceOptions) => SearchMatch[];
  handleReplace: (
    match: SearchMatch,
    replaceValue: string,
    searchTerm?: string,
    options?: FindReplaceOptions
  ) => Promise<void>;
  handleReplaceAll: (
    searchTerm: string, replaceValue: string, options: FindReplaceOptions
  ) => Promise<number>;
  handleCSVExport: () => void;
  handleCSVPreview: (file: File) => Promise<ImportPreview>;
  handleCSVImport: (file: File) => Promise<ImportResult>;
  handleAutoFillPreview: (
    mode: 'all' | 'selected'
  ) => ReturnType<typeof getAutoFillPreview> | null;
  handleConfirmAutoFill: (mode: 'all' | 'selected') => void;
  applyAutoFillToRows: (
    rowIndices: number[], labels: string[], mode?: QuickFillAutoFillMode
  ) => AutoFillApplyResult;
  handleSubmit: () => Promise<QuickFillSubmitOutcome>;
  saveRollbackToRevit: QuickFillMutationOperations['revitSync']['saveRollbackToRevit'];
  isSyncing: boolean;
  syncProgress: SyncProgress;
}

const EMPTY_PARAMETER_BINDING_KINDS: ReadonlyMap<string, QuickFillConfiguredBindingKind> = new Map();

export function useQuickFillTableOperations(
  parameterBindingKinds: ReadonlyMap<string, QuickFillConfiguredBindingKind> =
  EMPTY_PARAMETER_BINDING_KINDS
): UseQuickFillTableOperationsResult {
  const mutation = useQuickFillMutationOperations(parameterBindingKinds);
  const clipboard = useQuickFillClipboardOperations(mutation);
  const findReplace = useQuickFillFindReplaceOperations(mutation);
  const csv = useQuickFillCsvOperations(mutation);
  const autoFill = useQuickFillAutoFillOperations(mutation);

  const handleSubmit = useCallback(async (): Promise<QuickFillSubmitOutcome> => {
    const propertyMappings = (mutation.properties || [])
      .filter((property): property is typeof property & { revitParameter: string } =>
        Boolean(
          property?.checked &&
          property.label !== 'Name' &&
          property.revitParameter
        )
      )
      .map(mutation.buildPropertyMapping);
    const data = Array.isArray(mutation.quickFillData) ? mutation.quickFillData : [];
    const currentEditedCells = mutation.editedCells || {};
    const hasTrackedEdits = Object.values(currentEditedCells).some(Boolean);
    try {
      if (data.length > 0 && propertyMappings.length > 0) {
        const editedUpdates = buildEditedQuickFillCellUpdates(
          currentEditedCells,
          data,
          propertyMappings
        );
        const result = editedUpdates.length > 0
          ? await mutation.revitSync.saveCellsToRevit(data, editedUpdates)
          : hasTrackedEdits
            ? { totalUpdates: 0, successCount: 0, failureCount: 0, results: [] }
            : await mutation.revitSync.saveAllToRevit(data, propertyMappings);
        if (result.failureCount > 0) {
          mutation.snackbar.showWarning(
            `Saved ${result.successCount} changed values to Revit, ${result.failureCount} failed`
          );
        } else if (result.successCount > 0) {
          mutation.commitQuickFillDataAsBaseline();
          mutation.snackbar.showSuccess(`Saved ${result.successCount} changed values to Revit`);
        } else {
          mutation.commitQuickFillDataAsBaseline();
        }
        return { result };
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      mutation.snackbar.showWarning(`Continue without full save: ${message}`);
      return { result: null, warning: message };
    }
    return { result: null };
  }, [mutation]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const tagName = (event.target as HTMLElement).tagName;
      const isInputField = tagName === 'INPUT' || tagName === 'TEXTAREA';
      // Ctrl/Cmd+C is owned by the page interaction controller, which can see
      // both row and cell selection.
      if ((event.ctrlKey || event.metaKey) && event.key === 'd' &&
          mutation.selectedRows.length > 0 && !isInputField) {
        event.preventDefault();
        void clipboard.handleFillDown();
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'r' &&
          mutation.selectedRows.length > 0 && !isInputField) {
        event.preventDefault();
        void clipboard.handleFillRight();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clipboard, mutation.selectedRows]);

  return {
    selectedRows: mutation.selectedRows,
    replaceSelectedRows: mutation.replaceSelectedRows,
    handleTableDataChange: mutation.handleTableDataChange,
    stageCellUpdates: mutation.stageCellUpdates,
    handleSelectRow: mutation.handleSelectRow,
    handleSelectAllRows: mutation.handleSelectAllRows,
    handleBulkEdit: mutation.handleBulkEdit,
    ...clipboard,
    ...findReplace,
    ...csv,
    ...autoFill,
    handleSubmit,
    saveRollbackToRevit: mutation.revitSync.saveRollbackToRevit,
    isSyncing: mutation.revitSync.isSyncing,
    syncProgress: mutation.revitSync.progress,
  };
}
